import React from 'react';
import { SortableContainer } from 'react-sortable-hoc';

import { DraggableCustomPropertyRow } from 'src/features/crm/properties/CustomPropertyRow';
import { useAppDispatch } from 'src/hooks/useStore';
import { usePatchCompanyMutation } from 'src/services/api/companiesApi';
import { updateClientUser } from 'src/store/clients/actions';
import { AddressCustomFieldValues } from 'src/store/clients/types';

import {
  CustomFieldValueData,
  CustomPropertiesListProps,
} from '../clientDetailsTypes';

const CustomPropertiesList: React.FC<CustomPropertiesListProps> = ({
  setPropertiesMenuAnchor,
  anchoredCustomFieldId,
  clientUser,
  company,
  customFields,
  openUpdateCustomFieldMenu,
  onSaveProperty,
}) => {
  const dispatch = useAppDispatch();
  const [patchCompany] = usePatchCompanyMutation();
  const [activeFieldId, setActiveFieldId] = React.useState<string | null>(null);

  /**
   * This method get the custom field value
   * from client customFields by field's id.
   * @param customFieldId the id of the custom field
   */
  const getCustomFieldValue = (
    customFieldId: string,
  ): string | string[] | AddressCustomFieldValues | undefined => {
    if (clientUser?.fields?.customFields) {
      return clientUser.fields.customFields[customFieldId];
    } else if (company?.fields?.customFields) {
      return company.fields.customFields[customFieldId];
    }
    return undefined;
  };

  /**
   * This function updates client user data with
   * custom field value.
   * @param customFieldValue
   * @returns
   */
  const handleUpdateClientCustomFieldValue = async (
    customFieldData: CustomFieldValueData,
  ) => {
    if (!clientUser) {
      // Update company custom field value
      if (!company?.id) {
        return;
      }

      const updatedCompanyCustomFields = {
        ...company.fields.customFields,
        [customFieldData.fieldId]: customFieldData.value || '',
      };

      await patchCompany({
        companyId: company.id,
        customFields: updatedCompanyCustomFields,
      }).unwrap();
      return;
    }
    const updatedClientCustomFields = {
      ...clientUser.fields.customFields,
      [customFieldData.fieldId]: customFieldData.value,
    };
    const updateOptions = {
      showSuccessMessage: false,
      isOptimisticChange: true,
    };
    dispatch(
      updateClientUser(
        {
          userId: clientUser.id,
          cognitoFirstName: clientUser.fields.givenName,
          customFields: updatedClientCustomFields,
          companyId: clientUser.fields.companyId,
          companyName: clientUser.fields.companyName,
          additionalFields: clientUser.additionalFields,
        },
        updateOptions,
      ),
    );
  };

  return (
    <div className="flex flex-col">
      {customFields &&
        customFields.map((customField, index) => (
          <>
            <DraggableCustomPropertyRow
              onSaveProperty={onSaveProperty}
              key={customField.id}
              index={index}
              initialFieldValue={getCustomFieldValue(customField.id) || ''}
              field={{
                ...customField,
              }}
              onPropertyNameClicked={(
                ev: React.MouseEvent<HTMLElement, MouseEvent>,
              ) => {
                openUpdateCustomFieldMenu(ev, customField);
              }}
              onFieldValueUpdate={handleUpdateClientCustomFieldValue}
              setAsAnchor={customField.id === anchoredCustomFieldId}
              setAnchorEl={setPropertiesMenuAnchor}
              isEditing={activeFieldId === customField.id}
              onSetActiveFieldId={setActiveFieldId}
            />
          </>
        ))}
    </div>
  );
};

export const SortableCustomPropertiesList =
  SortableContainer(CustomPropertiesList);
